/**
 * Lightweight i18n context. Persists locale choice to localStorage so it
 * survives reloads, and exposes a `t(key, vars?)` lookup via the
 * `useTranslation` hook.
 *
 * Why custom instead of react-i18next:
 *   - We have a small fixed set of strings (no on-the-fly pluralisation
 *     beyond simple count display).
 *   - No SSR / namespacing / lazy-loading needed.
 *   - One file of translations is easier to keep in sync than a JSON tree.
 *
 * Fallback behaviour:
 *   - Missing key in the active locale → fall back to the `id` (default) entry.
 *   - Missing in both → return the key itself, so the UI doesn't render blank
 *     and we get a visible hint to add the translation.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { translations, type TranslationKey } from './translations';

export type Locale = 'id' | 'en';

const STORAGE_KEY = 'transpay_locale';
const DEFAULT_LOCALE: Locale = 'id';

type LocaleContextValue = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'id' || stored === 'en') return stored;
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* private mode / quota; ignore */
    }
    // Reflect on <html lang> so screen readers + browser autotranslate
    // recognise the active language.
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>): string => {
      const primary = translations[locale][key];
      const fallback = translations[DEFAULT_LOCALE][key];
      let str = primary ?? fallback ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
      }
      return str;
    },
    [locale],
  );

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useTranslation(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useTranslation must be used inside <LocaleProvider>');
  return ctx;
}
